import React from 'react';
import ContentfulWebForm from '@components/Shared/ContentfulWebForm/ContentfulWebForm';
import CTA from '@components/Shared/CTA/CTA';
import { submitForm } from '@services/formService';
import { extractColors } from '@utils/utils';
import { getCtaProperties } from '@utils/ctaUtils';

// I found this getCtaProperties function, so I used it here to share common code and I change the properties that need to be modified.
const renderCtas = (ctaList, additionalClassName = '') => {
  let results = ctaList
    ? ctaList.map((cta) => {
        return (
          <div className={`cta ${additionalClassName}`} key={cta.sys.id}>
            <CTA
              {...getCtaProperties(cta)}
              buttonAutomationId="cta-button-1"
              button2AutomationId="cta-button-2"
              buttonOpensToNewTab={false}
            />
          </div>
        );
      })
    : null;

  return results;
};

// Made a separate render for Dealer LP CTAs because they are not supposed to use all of the available CTA options (PCC-4966).
// I found this getCtaProperties function, so I used it here to share common code and I change the properties that need to be modified.
const renderDealerLpCtas = (ctaList, additionalClassName = '') => {
  let results = ctaList
    ? ctaList.map((cta) => {
        return (
          <div className={`cta ${additionalClassName}`} key={cta.sys.id}>
            <CTA
              {...getCtaProperties(cta)}
              subHeadingText={null}
              ctaText={null}
              buttonAutomationId="cta-button-1"
              button2AutomationId="cta-button-2"
              buttonOpensToNewTab={false}
            />
          </div>
        );
      })
    : null;

  return results;
};

export const renderWebForm = (
  formInfo,
  extData,
  stopRedirect = false,
  thankYouInfo,
  formData = null,
  prefix = 'Desktop',
  trackGAEvent = null
) => {
  if (!formInfo) {
    return null;
  }

  let redirectUrl =
    formInfo.firstFormButtonURL !== null &&
    typeof formInfo.firstFormButtonURL !== 'undefined' &&
    formInfo.firstFormButtonURL.startsWith('http')
      ? formInfo.firstFormButtonURL
      : '/' + formInfo.firstFormButtonURL;
  let redirectUrl2 =
    formInfo.secondFormButtonURL !== null &&
    typeof formInfo.secondFormButtonURL !== 'undefined' &&
    formInfo.secondFormButtonURL.startsWith('http')
      ? formInfo.secondFormButtonURL
      : '/' + formInfo.secondFormButtonURL;

  const formColors = extractColors(formInfo.buttonAndTextColors);

  return (
    <div id={`${prefix.toLowerCase()}-contentful-form`}>
      <ContentfulWebForm
        formClassName={`dealer-${prefix.toLowerCase()}-form`}
        prefix={prefix}
        key={formInfo.dataExtension}
        buttonText={formInfo.firstFormButtonText}
        buttonColor={formColors[0]}
        buttonTextColor={formColors[1]}
        button2Color={formInfo.secondButtonColor}
        button2Text={formInfo.secondFormButtonText}
        formContactFields={formInfo.formContactFields}
        headerIcon={formInfo.headerIcon}
        headerColor={formInfo.headerColor}
        headerText={formInfo.formHeader}
        subheaderText={formInfo.formSubHeader}
        formText={formInfo.formIntro}
        showDisclaimer={formInfo.disclaimerCheckbox}
        disclaimerText={formInfo.disclaimer}
        //set stopRedirect to true (it's false by default) if you want a thank you message
        // on the current page instead of a redirect
        stopRedirect={stopRedirect}
        thankYouInfo={thankYouInfo}
        redirectUrl={redirectUrl}
        redirectUrl2={redirectUrl2}
        dataExtension={formInfo.dataExtension}
        originWebFormType={formInfo.originWebFormType}
        originWebApp={formInfo.originWebApp}
        originWebPageType={formInfo.originWebPageType}
        originCampaign={formInfo.originCampaign}
        originWebFormCategory={formInfo.originWebFormCategory}
        originWebFormCategory2={formInfo.secondOriginWebFormCategory}
        customGAEventClass="form-submit-chcom"
        submitForm={submitForm}
        formData={formData !== null ? formData : {}}
        extData={extData && extData}
        trackGAEvent={trackGAEvent}
      />
    </div>
  );
};

export const renderWebsiteAreaContent = (
  contentfulData,
  extData,
  stopRedirect,
  thankYouInfo,
  formData = null,
  trackGAEvent = null
) => {
  if (typeof contentfulData === 'string' && contentfulData === '') {
    console.error(
      'Warning: Contentful Data not found. Check to ensure the ID is correct or Contentful is not down!'
    );
    return {};
  }

  let result = {};

  result.areaCtas =
    contentfulData &&
    contentfulData.areaContentCtas &&
    renderCtas(contentfulData.areaContentCtas);
  result.claytonRetailPromoCtas =
    contentfulData &&
    contentfulData.claytonRetailPromoCtas &&
    renderDealerLpCtas(contentfulData.claytonRetailPromoCtas);
  result.familyOfBrandsPromoCtas =
    contentfulData &&
    contentfulData.familyOfBrandsPromoCtas &&
    renderDealerLpCtas(contentfulData.familyOfBrandsPromoCtas);
  result.independentPromoCtas =
    contentfulData &&
    contentfulData.independentPromoCtas &&
    renderDealerLpCtas(contentfulData.independentPromoCtas);

  result.claytonRetailWebForm =
    contentfulData &&
    contentfulData.claytonRetailWebForm &&
    renderWebForm(
      contentfulData.claytonRetailWebForm,
      extData,
      stopRedirect,
      thankYouInfo,
      formData,
      'Desktop',
      trackGAEvent
    );
  result.familyOfBrandsWebForm =
    contentfulData &&
    contentfulData.familyOfBrandsWebForm &&
    renderWebForm(
      contentfulData.familyOfBrandsWebForm,
      extData,
      stopRedirect,
      thankYouInfo,
      formData,
      'Desktop',
      trackGAEvent
    );
  result.independentWebForm =
    contentfulData &&
    contentfulData.independentWebForm &&
    renderWebForm(
      contentfulData.independentWebForm,
      extData,
      stopRedirect,
      thankYouInfo,
      formData,
      'Desktop',
      trackGAEvent
    );

  result.claytonRetailWebFormModal =
    contentfulData &&
    contentfulData.claytonRetailWebForm &&
    renderWebForm(
      contentfulData.claytonRetailWebForm,
      extData,
      stopRedirect,
      thankYouInfo,
      formData,
      'Modal',
      trackGAEvent
    );
  result.familyOfBrandsWebFormModal =
    contentfulData &&
    contentfulData.familyOfBrandsWebForm &&
    renderWebForm(
      contentfulData.familyOfBrandsWebForm,
      extData,
      stopRedirect,
      thankYouInfo,
      formData,
      'Modal',
      trackGAEvent
    );
  result.independentWebFormModal =
    contentfulData &&
    contentfulData.independentWebForm &&
    renderWebForm(
      contentfulData.independentWebForm,
      extData,
      stopRedirect,
      thankYouInfo,
      formData,
      'Modal',
      trackGAEvent
    );

  result.claytonRetailWebFormData =
    contentfulData && contentfulData.claytonRetailWebForm;
  result.familyOfBrandsWebFormData =
    contentfulData && contentfulData.familyOfBrandsWebForm;
  result.independentWebFormData =
    contentfulData && contentfulData.independentWebForm;

  return result;
};
